/**
 * Sidebar Component
 *
 * Navigation sidebar component that displays:
 * - Main navigation menu items
 * - Role-based menu visibility (Admin Management for SuperAdmins only)
 * - Active route highlighting
 * - Expandable submenus for Reports
 * - Responsive behavior (drawer mode on mobile)
 *
 * @example
 * ```html
 * <app-sidebar></app-sidebar>
 * ```
 */

import { Component, OnInit, OnDestroy, Output, EventEmitter, HostListener, ElementRef, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import { SidebarModule } from 'primeng/sidebar';
import { PanelMenuModule } from 'primeng/panelmenu';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';

import { UserService } from '../../../../core/services/user.service';
import { ViewModeService } from '../../../../core/services/view-mode.service';
import { TranslationService } from '../../../../core/services/translation.service';
import { LanguageService } from '../../../../core/services/language.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SidebarModule,
    PanelMenuModule,
    TooltipModule
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {
  /**
   * Navigation menu items
   */
  menuItems: MenuItem[] = [];

  /**
   * Flag to determine if user is super admin
   */
  isSuperAdmin = false;

  public languageService = inject(LanguageService);

  /**
   * Whether sidebar is collapsed (icons only)
   */
  isCollapsed = true;

  /**
   * Emits when sidebar collapse state changes
   */
  @Output() collapsedChange = new EventEmitter<boolean>();

  /**
   * Currently active menu item for first level popup
   */
  activeItem: MenuItem | null = null;

  /**
   * Currently active submenu item for second level popup
   */
  activeSubItem: MenuItem | null = null;

  /**
   * Position for the first level popup
   */
  popupPosition = { top: 0 };

  /**
   * Position for the second level popup
   */
  subPopupPosition = { top: 0 };

  /**
   * Subject for managing component subscriptions
   */
  private destroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private viewModeService: ViewModeService,
    private router: Router,
    private elementRef: ElementRef,
    private translationService: TranslationService
  ) {
    // Rebuild menu whenever the language changes so labels are re-translated
    effect(() => {
      this.translationService.currentLanguage();
      this.translationService.translationsLoaded();
      this.setupMenu();
    });
  }

  /**
   * Close popups when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeAllPopups();
    }
  }

  /**
   * Initializes the component
   * Sets up menu items based on user role
   */
  ngOnInit(): void {
    // Check if user is super admin
    this.isSuperAdmin = this.userService.isSuperAdmin();

    // Subscribe to user and view mode changes to update menu dynamically
    combineLatest([
      this.userService.currentUser$,
      this.viewModeService.viewMode$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isSuperAdmin = this.userService.isSuperAdmin();
        this.setupMenu();
      });

    // Re-evaluate active route styles when navigation completes
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.applyActiveStyles();
      });

    // Initial menu setup
    this.setupMenu();
  }

  /**
   * Cleanup when component is destroyed
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets up the navigation menu items based on current view mode
   * Dispatches to default or branch menu builder
   */
  private setupMenu(): void {
    const viewMode = this.viewModeService.currentViewMode;

    const user = this.userService.currentUser;
    const canViewBranch = this.isSuperAdmin
      || (this.userService.isAdmin() && (user?.categories_count ?? 0) > 0);

    if (viewMode === 'branch' && canViewBranch) {
      this.menuItems = this.getBranchMenuItems();
    } else {
      this.menuItems = this.getDefaultMenuItems();
    }
    this.applyActiveStyles();
  }

  /**
   * Marks the menu item matching the current route with 'active-route' styleClass.
   * PrimeNG's internal activeItem resets when menuItems are replaced,
   * so we manually track active state via styleClass on the header div.
   */
  private applyActiveStyles(): void {
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    this.menuItems = this.menuItems.map(item => ({
      ...item,
      styleClass: this.isRouteActive(item, currentUrl) ? 'active-route' : undefined
    }));
  }

  /**
   * Checks if a menu item's routerLink matches the current URL
   */
  private isRouteActive(item: MenuItem, currentUrl: string): boolean {
    if (!item.routerLink) return false;
    const path = Array.isArray(item.routerLink) ? item.routerLink[0] : item.routerLink;
    if (item.routerLinkActiveOptions?.exact) {
      return currentUrl === path;
    }
    return currentUrl.startsWith(path);
  }

  /**
   * Shorthand helper to translate a key via the TranslationService.
   *
   * @param key - Translation key
   * @returns Translated string
   */
  private t(key: string): string {
    return this.translationService.translate(key);
  }

  /**
   * Returns menu items for the default (Company) view
   * Includes role-based visibility for Admin Management and Branch Management
   */
  private getDefaultMenuItems(): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: this.t('sidebar.dashboard'),
        icon: 'pi pi-home',
        routerLink: ['/dashboard'],
        routerLinkActiveOptions: { exact: true }
      },
      {
        label: this.t('sidebar.schools'),
        icon: 'pi pi-building',
        routerLink: ['/schools']
      },
      {
        label: this.t('sidebar.reports'),
        icon: 'pi pi-chart-bar',
        items: [
          {
            label: this.t('sidebar.attendance'),
            items: [
              {
                label: this.t('sidebar.summary'),
                styleClass: 'nested-child',
                routerLink: ['/reports/attendance/summary']
              },
              {
                label: this.t('sidebar.comparison'),
                styleClass: 'nested-child',
                routerLink: ['/reports/attendance/comparison']
              },
              {
                label: this.t('sidebar.trends'),
                styleClass: 'nested-child',
                routerLink: ['/reports/attendance/trends']
              }
            ]
          },
          {
            label: this.t('sidebar.dismissal'),
            items: [
              {
                label: this.t('sidebar.summary'),
                styleClass: 'nested-child',
                routerLink: ['/reports/dismissal/summary']
              },
              {
                label: this.t('sidebar.comparison'),
                styleClass: 'nested-child',
                routerLink: ['/reports/dismissal/comparison']
              },
              {
                label: this.t('sidebar.byType'),
                styleClass: 'nested-child',
                routerLink: ['/reports/dismissal/by-type']
              }
            ]
          },
          {
            label: this.t('sidebar.engagement'),
            items: [
              {
                label: this.t('sidebar.parents'),
                styleClass: 'nested-child',
                routerLink: ['/reports/engagement/parents']
              },
              {
                label: this.t('sidebar.staff'),
                styleClass: 'nested-child',
                routerLink: ['/reports/engagement/staff']
              }
            ]
          },
          {
            label: this.t('sidebar.growth'),
            items: [
              {
                label: this.t('sidebar.studentGrowth'),
                styleClass: 'nested-child',
                routerLink: ['/reports/growth/students']
              },
              {
                label: this.t('sidebar.userGrowth'),
                styleClass: 'nested-child',
                routerLink: ['/reports/growth/users']
              }
            ]
          }
        ]
      },
      {
        label: this.t('sidebar.profile'),
        icon: 'pi pi-user',
        routerLink: ['/profile']
      }
    ];

    if (this.isSuperAdmin) {
      // Admin Management (SuperAdmin only)
      items.push({
        label: this.t('sidebar.adminManagement'),
        icon: 'pi pi-users',
        routerLink: ['/admin-management']
      });
    }

    return items;
  }

  /**
   * Returns menu items for the Branch view mode
   * Shows aggregate branch dashboards and reports
   */
  private getBranchMenuItems(): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: this.t('sidebar.branchView'),
        icon: 'pi pi-home',
        routerLink: ['/branch-view/dashboard'],
        routerLinkActiveOptions: { exact: true }
      }
    ];

    if (this.isSuperAdmin) {
      items.push({
        label: this.t('sidebar.branchManagement'),
        icon: 'pi pi-sitemap',
        routerLink: ['/branch-view/branch-management']
      });
    }

    return items;
  }

  /**
   * Toggles sidebar collapsed state
   */
  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.closeAllPopups();
    this.collapsedChange.emit(this.isCollapsed);
  }

  /**
   * Show first level popup menu for collapsed sidebar item
   */
  showPopup(item: MenuItem, event: MouseEvent): void {
    if (this.isCollapsed && item.items && item.items.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      this.popupPosition.top = rect.top;
      this.activeItem = item;
      this.activeSubItem = null;
    }
  }

  /**
   * Show second level popup for submenu item
   */
  showSubPopup(subItem: MenuItem, event: MouseEvent): void {
    if (subItem.items && subItem.items.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      this.subPopupPosition.top = rect.top;
      this.activeSubItem = subItem;
    }
  }

  /**
   * Close all popups
   */
  closeAllPopups(): void {
    this.activeItem = null;
    this.activeSubItem = null;
  }

  /**
   * Navigate to route and close all popups
   */
  navigateAndClose(routerLink: string[]): void {
    this.router.navigate(routerLink);
    this.closeAllPopups();
  }
}
